/* eslint-disable no-console */
import { useFieldArray, useForm, useFormContext, UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Trash2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../components/ui/form';
import { FormSchema } from '../Create/Schema';
import Input from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { DialogClose, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import usePartner from '../../../stores/usePartner';
import { IPartner } from '../../../types/types';

interface FormContactPersonProps {
  index: number;
  onRemove: (index: number) => void;
  form: UseFormReturn<z.infer<typeof FormSchema>>;
}

function FormContactPerson({ index, onRemove, form }: FormContactPersonProps) {
  const { control } = useFormContext();

  return (
    <>
      <FormField
        name={`contact_persons.${index}.name`} // Nama field
        control={control} // Kontrol dari useForm
        render={({ field }) => (
          <FormItem className="text-left">
            <FormLabel className="text-text font-semibold">
              Name <span className="text-red-600">*</span>
            </FormLabel>
            <Input
              {...field}
              id="name"
              placeholder="Name Partner"
              className={`${form.formState.errors.contact_persons?.[index]?.name ? 'border-red-500' : ''} text-text`}
              required
            />
          </FormItem>
        )}
      />
      <FormField
        name={`contact_persons.${index}.email`} // Nama field
        control={control} // Kontrol dari useForm
        render={({ field }) => (
          <FormItem className="text-left">
            <FormLabel className="text-text font-semibold">Email</FormLabel>
            <Input
              {...field}
              type="email"
              placeholder="Email Partner"
              className={`${form.formState.errors.contact_persons?.[index]?.email ? 'border-red-500' : ''} text-text`}
            />
          </FormItem>
        )}
      />
      <div className="flex flex-col md:flex-row gap-4">
        <FormField
          name={`contact_persons.${index}.phone`} // Nama field
          control={control} // Kontrol dari useForm
          render={({ field }) => (
            <FormItem className="text-left">
              <FormLabel className="text-text font-semibold">Phone</FormLabel>
              <Input
                {...field}
                placeholder="Phone Number"
                className={`${form.formState.errors.contact_persons?.[index]?.phone ? 'border-red-500' : ''} text-text`}
                value={field.value ?? ''}
              />
            </FormItem>
          )}
        />
        <FormField
          name={`contact_persons.${index}.position`} // Nama field
          control={control} // Kontrol dari useForm
          render={({ field }) => (
            <FormItem className="text-left">
              <FormLabel className="text-text font-semibold">Position</FormLabel>
              <Input
                {...field}
                placeholder="Position"
                className={`${
                  form.formState.errors.contact_persons?.[index]?.position ? 'border-red-500' : ''
                } text-text`}
                value={field.value ?? ''}
              />
            </FormItem>
          )}
        />
      </div>
      <Button
        type="button"
        variant="outline"
        className="text-red-600 hover:text-red-600 hover:bg-red-50 border-red-600 w-max ml-auto"
        onClick={() => onRemove(index)}
      >
        <Trash2 size={16} />
      </Button>
    </>
  );
}

export default function EditFormPartner({ partner, onSuccess }: { partner: IPartner; onSuccess?: () => void }) {
  const { updatePartner, loading } = usePartner();

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      partner_number: partner?.partner_number,
      user_id: partner?.user_id,
      company_type: partner?.company_type || '',
      name: partner?.name || '',
      email: partner?.email || '',
      phone: partner?.phone || '',
      contact_persons: partner?.contact_persons || [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'contact_persons',
  });

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    try {
      if (!loading) {
        const response = await updatePartner(partner.partner_id as string, data as IPartner);
        const { success, message } = response;
        if (!success) {
          throw new Error(message);
        }
        onSuccess?.();
      }
    } catch (error) {
      console.error(error);
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <DialogHeader>
          <DialogTitle className="text-left mb-2 md:mb-4">Edit Partner</DialogTitle>
          <DialogDescription>
            <Tabs defaultValue="company">
              <TabsList>
                <TabsTrigger value="company">Company Information</TabsTrigger>
                <TabsTrigger value="contact">Contact Person</TabsTrigger>
              </TabsList>
              <TabsContent value="company" className="p-2 flex flex-col gap-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <FormField
                    name="user_id" // Nama field
                    control={form.control} // Kontrol dari useForm
                    render={({ field }) => (
                      <FormItem className="text-left flex-1 hidden">
                        <Input className="text-text" {...field} />
                      </FormItem>
                    )}
                  />
                  <FormField
                    name="partner_number" // Nama field
                    control={form.control} // Kontrol dari useForm
                    render={({ field }) => (
                      <FormItem className="text-left flex-1">
                        <FormLabel htmlFor="partner_number" className="text-text font-semibold">
                          Partner No. <span className="text-red-600">*</span>
                        </FormLabel>
                        <Input
                          className={`${form.formState.errors.partner_number ? 'border-red-500' : ''} text-text`}
                          id="partner_number"
                          placeholder="Enter Partner Number"
                          {...field}
                          required
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    name="company_type" // Nama field
                    control={form.control} // Kontrol dari useForm
                    render={({ field }) => (
                      <FormItem className="text-left flex-1">
                        <FormLabel className="text-text font-semibold">
                          Company Type <span className="text-red-600">*</span>
                        </FormLabel>
                        <Select onValueChange={field.onChange} value={field.value} required>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Choose Company Type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Individual">Individual</SelectItem>
                            <SelectItem value="CV">CV</SelectItem>
                            <SelectItem value="PT">PT</SelectItem>
                            <SelectItem value="Others">Others</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  name="name" // Nama field
                  control={form.control} // Kontrol dari useForm
                  render={({ field }) => (
                    <FormItem className="text-left">
                      <FormLabel htmlFor="name" className="text-text font-semibold">
                        Name <span className="text-red-600">*</span>
                      </FormLabel>
                      <Input
                        className={`${form.formState.errors.name ? 'border-red-500' : ''} text-text`}
                        id="name"
                        placeholder="Name Partner"
                        {...field}
                        required
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  name="email" // Nama field
                  control={form.control} // Kontrol dari useForm
                  render={({ field }) => (
                    <FormItem className="text-left">
                      <FormLabel htmlFor="email" className="text-text font-semibold">
                        Email
                      </FormLabel>
                      <Input
                        className={`${form.formState.errors.email ? 'border-red-500' : ''} text-text`}
                        id="email"
                        type="email"
                        placeholder="Email Partner"
                        {...field}
                        value={field.value ?? ''}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  name="phone" // Nama field
                  control={form.control} // Kontrol dari useForm
                  render={({ field }) => (
                    <FormItem className="text-left">
                      <FormLabel htmlFor="phone" className="text-text font-semibold">
                        Phone <span className="text-red-600">*</span>
                      </FormLabel>
                      <Input
                        className={`${form.formState.errors.phone ? 'border-red-500' : ''} text-text`}
                        id="phone"
                        placeholder="Phone Number"
                        {...field}
                        required
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </TabsContent>
              <TabsContent value="contact" className="p-2 flex flex-col gap-4">
                {fields.map((field, index) => (
                  <FormContactPerson key={field.id} index={index} onRemove={remove} form={form} />
                ))}
                <Button
                  variant="orangeOutline"
                  className="w-max mx-auto mb-4"
                  onClick={() =>
                    append({
                      name: '',
                      email: '',
                      phone: '',
                      position: '',
                    })
                  }
                >
                  Add Contact Information
                </Button>
              </TabsContent>
            </Tabs>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex flex-row justify-end gap-2">
          <Button variant="default" type="submit" disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </Button>
          <DialogClose>
            <Button variant="outline" type="button">
              Cancel
            </Button>
          </DialogClose>
        </DialogFooter>
      </form>
    </Form>
  );
}
